// =============================================================================
// Secondary candidates — per-pattern fit summary, disambiguator, and weight.
//
// When mixed_picture wins, arbitrate.ts hands over the surviving pattern ids.
// This module turns each id into a SecondaryCandidate populated with copy
// tailored to the user's actual inputs (so the candidate card reads
// "Your ferritin at 40 μg/L sits in the Stage 1 band of 30–49 μg/L."
// rather than a generic placeholder).
//
// Design constraints:
//   - No engine state of its own; pure functions on (patternId, inputs).
//   - Copy is short, single-sentence per field, written for layperson.
//   - Disambiguating test names a concrete next test by name.
//   - Weight is a coarse 0–1 score used only for ranking ties; not a
//     calibrated probability. Renderer sorts candidates descending.
// =============================================================================

import type { LabInputs, SecondaryCandidate, SeverityTier } from './types.js';
import type { PatternId } from './patterns.js';

/** Build a SecondaryCandidate for `patternId` given the user's inputs. */
export function buildSecondaryCandidate(
  patternId: PatternId,
  inputs: LabInputs,
): SecondaryCandidate {
  return {
    patternId,
    fitSummary: fitSummaryFor(patternId, inputs),
    disambiguatingTest: disambiguatingTestFor(patternId, inputs),
    weight: weightFor(patternId, inputs),
    severity: severityFor(patternId),
  };
}

// -----------------------------------------------------------------------------
// fitSummary — one sentence, tailored to inputs
// -----------------------------------------------------------------------------

function fitSummaryFor(patternId: PatternId, inputs: LabInputs): string {
  switch (patternId) {
    case 'stage_0_iron_replete':
      return `Your ferritin at ${fmtFerritin(inputs)} sits at or above the research-based replete threshold for your life stage.`;
    case 'stage_1_incipient_id':
      return `Your ferritin at ${fmtFerritin(inputs)} sits in the early-decline band — above the iron-shortage threshold but below the comfortably-replete level.`;
    case 'stage_2_iron_depletion_no_anemia':
      return `Your ferritin at ${fmtFerritin(inputs)} sits in the depletion band — reserves are genuinely low, but red-cell production has not yet been affected.`;
    case 'stage_3_iron_deficient_erythropoiesis':
      return `Your ferritin at ${fmtFerritin(inputs)} is below the iron-shortage threshold for your life stage, and red-cell production is starting to feel the constraint.`;
    case 'stage_4_iron_deficiency_anemia':
      return `Your ferritin and hemoglobin both sit below the research thresholds for iron deficiency anemia.`;
    case 'inflammation_masks_iron_status':
      return `Inflammation is in the picture (CRP elevated or recent illness), so the ferritin reading cannot be taken at face value.`;
    case 'functional_iron_deficiency':
      return `Your ferritin is normal or high but TSAT is low and inflammation is present — the signature of iron locked away from the cells that need it.`;
    case 'anemia_of_chronic_disease_classic':
      return `You are anemic with normal-to-high ferritin, low TSAT, and inflammation — the research-defined signature of anemia of chronic disease.`;
    case 'iron_overload_trigger_premenopausal':
      return `Your ferritin and TSAT are both above the premenopausal evaluation thresholds (ferritin > 200 μg/L paired with TSAT > 45 %).`;
    case 'iron_overload_trigger_postmenopausal':
      return `Your ferritin and TSAT are both above the postmenopausal evaluation thresholds (ferritin > 300 μg/L paired with TSAT > 50 %).`;
    case 'very_high_ferritin_any_stage':
      return `Your ferritin is above 1000 μg/L, which the research flags for evaluation regardless of other markers.`;
    case 'microcytic_normal_iron_studies':
      return `Your MCV at ${fmtMcv(inputs)} fL sits below 80 fL even though your iron studies look adequate — a pattern that can point to a hemoglobin variant rather than iron deficiency.`;
    case 'macrocytic_anemia_signal':
      return `Your MCV at ${fmtMcv(inputs)} fL is above 100 fL with low hemoglobin — a pattern that points away from iron and toward B12, folate, or thyroid causes.`;
    case 'anemia_with_replete_ferritin':
      return `Your hemoglobin sits below the reference range (anemia present) but your ferritin at ${fmtFerritin(inputs)} is above the depletion threshold — iron stores aren't the bottleneck, so something else is driving the anemia.`;
    default:
      return 'This pattern matched the trigger conditions for your inputs.';
  }
}

// -----------------------------------------------------------------------------
// disambiguatingTest — concrete next-step test naming
// -----------------------------------------------------------------------------

function disambiguatingTestFor(patternId: PatternId, _inputs: LabInputs): string {
  switch (patternId) {
    case 'stage_0_iron_replete':
    case 'stage_1_incipient_id':
    case 'stage_2_iron_depletion_no_anemia':
      return 'A repeat ferritin in 3–6 months would show whether stores are stable or trending.';
    case 'stage_3_iron_deficient_erythropoiesis':
      return 'A full iron panel (TSAT + serum iron + TIBC) would confirm iron-restricted erythropoiesis.';
    case 'stage_4_iron_deficiency_anemia':
      return 'A reticulocyte count and a full iron panel would confirm the anemia is iron-driven and gauge how active the deficiency is.';
    case 'inflammation_masks_iron_status':
      return 'A repeat ferritin 2–4 weeks after the inflammation clears would give a readable value.';
    case 'functional_iron_deficiency':
      return 'Soluble transferrin receptor (sTfR) is less affected by inflammation than ferritin and would clarify whether iron deficiency is also present.';
    case 'anemia_of_chronic_disease_classic':
      return 'sTfR or the sTfR/log-ferritin index would help distinguish anemia of chronic disease from a coexisting iron deficiency.';
    case 'iron_overload_trigger_premenopausal':
    case 'iron_overload_trigger_postmenopausal':
      return 'A fasting morning repeat draw and HFE genotyping would clarify whether this is hereditary haemochromatosis or another cause.';
    case 'very_high_ferritin_any_stage':
      return 'A clinician-led workup would sort the cause: hereditary haemochromatosis, alcohol, liver, metabolic, or inflammation.';
    case 'microcytic_normal_iron_studies':
      return 'A hemoglobin electrophoresis would distinguish iron-deficient erythropoiesis from a thalassemia or other hemoglobinopathy trait.';
    case 'macrocytic_anemia_signal':
      return 'B12, folate, and thyroid panels would point to the cause of the macrocytosis; iron is unlikely to be the driver.';
    case 'anemia_with_replete_ferritin':
      return 'MCV (red-cell size) sorts the differential into microcytic, normocytic, or macrocytic — the standard first move for anemia with non-deficient ferritin.';
    default:
      return 'A clinician conversation with your full medical history would help separate the candidates.';
  }
}

// -----------------------------------------------------------------------------
// weight — for ranking only (not a calibrated probability)
// -----------------------------------------------------------------------------

/**
 * Coarse weight used to order candidates in the renderer. Patterns that
 * route to a clinical pathway (overload, FID, ACD, severe anemia) outweigh
 * routine staging patterns; morphology patterns sit alongside staging.
 */
function weightFor(patternId: PatternId, _inputs: LabInputs): number {
  switch (patternId) {
    case 'stage_4_iron_deficiency_anemia':
    case 'anemia_of_chronic_disease_classic':
    case 'functional_iron_deficiency':
    case 'iron_overload_trigger_premenopausal':
    case 'iron_overload_trigger_postmenopausal':
    case 'very_high_ferritin_any_stage':
    case 'macrocytic_anemia_signal':
    case 'anemia_with_replete_ferritin':
      return 0.7;
    case 'microcytic_normal_iron_studies':
    case 'stage_3_iron_deficient_erythropoiesis':
      return 0.55;
    case 'stage_2_iron_depletion_no_anemia':
    case 'stage_1_incipient_id':
      return 0.4;
    case 'stage_0_iron_replete':
      return 0.3;
    case 'inflammation_masks_iron_status':
      return 0.5;
    default:
      return 0.5;
  }
}

// -----------------------------------------------------------------------------
// severity — per-candidate, mirrors the engine's per-pattern severity rules
// -----------------------------------------------------------------------------

function severityFor(patternId: PatternId): SeverityTier {
  // Mirrors the prompt-list in arbitrate.computeSeverity. If the engine's
  // logic moves, update both. (Both files import the same PatternId union
  // so the type checker catches drift.)
  const promptPatterns: PatternId[] = [
    'iron_overload_trigger_premenopausal',
    'iron_overload_trigger_postmenopausal',
    'very_high_ferritin_any_stage',
    'anemia_of_chronic_disease_classic',
    'functional_iron_deficiency',
    'stage_4_iron_deficiency_anemia',
    'macrocytic_anemia_signal',
    'anemia_with_replete_ferritin',
  ];
  return promptPatterns.includes(patternId) ? 'prompt' : 'routine';
}

// -----------------------------------------------------------------------------
// Formatters
// -----------------------------------------------------------------------------

function fmtFerritin(inputs: LabInputs): string {
  if (typeof inputs.ferritin !== 'number') return '—';
  return `${inputs.ferritin} μg/L`;
}

function fmtMcv(inputs: LabInputs): string {
  if (typeof inputs.mcv !== 'number') return '—';
  return String(inputs.mcv);
}
